/**
 * Shortcuts Help Component
 * Shows available keyboard shortcuts in a popup
 */
import React, { useState } from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Keyboard as KeyboardIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

interface ShortcutItem {
  keys: string[];
  description: string;
}

const shortcuts: ShortcutItem[] = [
  { keys: ["Ctrl", "S"], description: "Save" },
  { keys: ["Ctrl", "Z"], description: "Undo" },
  { keys: ["Ctrl", "Y"], description: "Redo" },
  { keys: ["Ctrl", "C"], description: "Copy element" },
  { keys: ["Ctrl", "V"], description: "Paste element" },
  { keys: ["Ctrl", "D"], description: "Duplicate element" },
  { keys: ["Delete"], description: "Delete element" },
  { keys: ["Backspace"], description: "Delete element" },
];

const KeyBadge: React.FC<{ children: string }> = ({ children }) => (
  <Chip
    label={children}
    size="small"
    sx={{
      height: 24,
      minWidth: 32,
      borderRadius: 1,
      bgcolor: "grey.200",
      color: "grey.800",
      fontWeight: 600,
      fontSize: 11,
      fontFamily: "monospace",
    }}
  />
);

export const ShortcutsHelp: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="Keyboard shortcuts" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            color: "grey.600",
            "&:hover": { bgcolor: "grey.200" },
          }}
        >
          <KeyboardIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <KeyboardIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight={600}>Keyboard Shortcuts</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {shortcuts.map((shortcut, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1,
                  px: 1.5,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {shortcut.description}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  {shortcut.keys.map((key, i) => (
                    <React.Fragment key={i}>
                      <KeyBadge>{key}</KeyBadge>
                      {i < shortcut.keys.length - 1 && (
                        <Typography variant="caption" color="grey.400">+</Typography>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2, textAlign: "center" }}>
            Select an element first for copy/paste/delete
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShortcutsHelp;
